import {
  Message,
  MessageCall,
  MessageCleanup,
  MessageExposeAPI,
  MessageResponse,
  MessageResponseError,
  MessageType,
} from './Message';
import { errorMessage } from './error';

const buildFunctionMarkerKey = (correlationId: string, path: string) => {
  return `${correlationId}_${path}`;
};

export type IMessageCallbacks = Partial<{
  [MessageType.Cleanup]: (message: MessageCleanup) => void;
  [MessageType.Response]: (
    message: MessageResponse | MessageResponseError,
  ) => void;
  [MessageType.Call]: (message: MessageCall) => void;
  [MessageType.ExposeAPI]: (message: MessageExposeAPI) => void;
}>;

export abstract class ThreadChannel {
  protected abstract messageEvents: IMessageCallbacks;

  #registry = new FinalizationRegistry<{
    functionMarkerKey: string;
    callId?: string;
  }>((params) => {
    this.postMessage(new MessageCleanup(params));
  });

  #messageEvents: IMessageCallbacks = {
    [MessageType.Cleanup]: (message) => {
      const functionMarkerKey = message.payload.functionMarkerKey;
      const callId = message.payload.callId;

      /* eslint-disable @typescript-eslint/no-dynamic-delete */
      delete this.#functionMarkers[functionMarkerKey];

      if (callId) {
        /* eslint-disable @typescript-eslint/no-dynamic-delete */
        delete this.#pendingFunctionResponse[callId];
      }
    },
    /**
     * Return function response between threads
     */
    [MessageType.Response]: async (message) => {
      const callId = message.payload.callId;
      const _errorMessage = (message as MessageResponseError).payload
        .errorMessage;

      if (_errorMessage) {
        const errorStack = (message as MessageResponseError).payload.errorStack;

        if (errorStack) {
          const error = new Error(_errorMessage);
          error.stack = (message as MessageResponseError).payload.errorStack;

          this.#pendingFunctionResponse[callId]?.reject(error);
        } else {
          this.#pendingFunctionResponse[callId]?.reject(_errorMessage);
        }
      } else {
        const response = (message as MessageResponse).payload.response;
        this.#pendingFunctionResponse[callId]?.resolve(response);
      }

      /* eslint-disable @typescript-eslint/no-dynamic-delete */
      delete this.#pendingFunctionResponse[callId];
    },
    /**
     * Calling function between threads
     */
    [MessageType.Call]: async (message) => {
      const functionMarkerKey = message.payload.functionMarkerKey;
      const marker = this.#functionMarkers[functionMarkerKey];

      if (!marker) {
        this.postMessage(
          new MessageResponseError({
            callId: message.payload.callId,
            errorMessage: errorMessage('function is not available'),
          }),
        );

        return;
      }

      /**
       * TODO: for debugging purposes – check if original function still exists
       */
      try {
        const response = await marker.value(...(message.payload.props ?? []));

        this.postMessage(
          new MessageResponse({
            callId: message.payload.callId,
            response,
          }),
        );
      } catch (error: unknown) {
        this.postMessage(
          new MessageResponseError({
            callId: message.payload.callId,
            /**
             * for better DX we need to pass Error object to keep stack trace available
             * but in some cases our custom error messages are not serialized correctly
             */
            errorMessage: (error as Error).message,
            errorStack: (error as Error).stack,
          }),
        );
      }
    },
  };

  #functionMarkers: Record<
    string,
    {
      path: string;
      value: (...args: any) => any;
    }
  > = {};

  #pendingFunctionResponse: Record<
    string,
    { resolve: Function; reject: Function }
  > = {};

  protected queue: Message[] = [];

  constructor(protected port: MessagePort) {
    this.port.onmessage = (event) => {
      this.onMessage(event);
    };
  }

  protected onMessage(event: MessageEvent) {
    const message = Message.fromEvent(event.data, (correlationId, path) => {
      const replacedFunction = this.replaceTransferredFunction(
        correlationId,
        path,
      );

      this.#registry.register(replacedFunction, {
        functionMarkerKey: buildFunctionMarkerKey(correlationId, path),
        // callId: message.payload?.callId,
      });

      return replacedFunction;
    });

    this.#messageEvents[message.type]?.(message);
    this.messageEvents[message.type]?.(message);
  }

  protected postMessage(message: Message) {
    this.port.postMessage(
      message.serialize(this.registerTransferredFunction.bind(this)),
    );
  }

  registerTransferredFunction(
    correlationId: string,
    path: string,
    value: (...args: any) => any,
  ) {
    const functionMarkerKey = buildFunctionMarkerKey(correlationId, path);

    this.#functionMarkers[functionMarkerKey] = {
      path,
      value,
    };
  }

  replaceTransferredFunction(correlationId: string, path: string) {
    return (...props: any[]) => {
      const callId = `CALL-${crypto.randomUUID()}`;

      this.postMessage(
        new MessageCall({
          callId,
          functionMarkerKey: buildFunctionMarkerKey(correlationId, path),
          props,
        }),
      );

      return new Promise((resolve, reject) => {
        // TODO: check never resolved promise
        this.#pendingFunctionResponse[callId] = { resolve, reject };
      });
    };
  }

  protected flush() {
    this.queue.forEach((message) => {
      this.postMessage(message);
    });

    this.queue = [];
  }

  /**
   * TODO:
   * before message port is disposed
   * we need to cleanup all pending promises (probably reject or cancel with AbortController)
   * note, this function should be async, and we need
   * to wait it before dispose port and create a new port
   */
  // shutdown() {
  //   this.postMessage(new MessageShutdown());
  //
  //   Object.keys(this.#pendingFunctionResponse).forEach((key) => {
  //     const value = this.#pendingFunctionResponse[key]
  //     if (value && value.reject && value.resolve) {
  //       value.reject(new Error('transport port shutdown'));
  //     }
  //   })
  // }
}
